import { useEffect, useState } from "react";
import { Send, Inbox, CheckCheck, Eye, XCircle, MessageSquare, Loader2 } from "lucide-react";
import api from "@/dashboard/api";

const StatCard = ({ icon: Icon, label, value, color, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-brand-border p-6 card-soft">
    <div className={`w-11 h-11 rounded-lg flex items-center justify-center mb-4 ${color}`}>
      <Icon className="w-5 h-5" strokeWidth={2} />
    </div>
    <div className="font-display font-bold text-3xl text-brand-ink">{value}</div>
    <div className="text-sm text-brand-muted mt-1">{label}</div>
  </div>
);

const badge = (status) => {
  const map = {
    sent: "bg-blue-50 text-blue-700",
    delivered: "bg-green-50 text-green-700",
    read: "bg-emerald-50 text-emerald-700",
    failed: "bg-red-50 text-red-700",
    received: "bg-slate-100 text-slate-700",
  };
  return map[status] || "bg-slate-100 text-slate-600";
};

const Overview = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const load = () => api.get("/whatsapp/dashboard-stats").then((r) => setStats(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  if (!stats) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>;
  }

  return (
    <div data-testid="dashboard-overview">
      <h1 className="font-display font-bold text-3xl text-brand-ink mb-1">Dashboard</h1>
      <p className="text-brand-muted mb-8">Live WhatsApp messaging overview.</p>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
        <StatCard icon={Send} label="Messages Sent" value={stats.total_sent} color="bg-blue-50 text-brand-blue" testid="stat-sent" />
        <StatCard icon={Inbox} label="Messages Received" value={stats.total_received} color="bg-slate-100 text-slate-700" testid="stat-received" />
        <StatCard icon={CheckCheck} label="Delivered" value={stats.delivered} color="bg-green-50 text-green-700" testid="stat-delivered" />
        <StatCard icon={Eye} label="Read" value={stats.read} color="bg-emerald-50 text-emerald-700" testid="stat-read" />
        <StatCard icon={XCircle} label="Failed" value={stats.failed} color="bg-red-50 text-brand-red" testid="stat-failed" />
        <StatCard icon={MessageSquare} label="Conversations" value={stats.conversations} color="bg-brand-red-light text-brand-red" testid="stat-conversations" />
      </div>

      <div className="bg-white rounded-2xl border border-brand-border card-soft overflow-hidden">
        <div className="px-6 py-4 border-b border-brand-border font-display font-bold text-brand-ink">
          Recent Activity
        </div>
        {stats.recent.length === 0 ? (
          <div className="px-6 py-12 text-center text-brand-muted">No messages yet.</div>
        ) : (
          <div className="divide-y divide-brand-border">
            {stats.recent.map((m) => (
              <div key={m.id} className="px-6 py-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${m.direction === "inbound" ? "bg-slate-100 text-slate-700" : "bg-blue-50 text-brand-blue"}`}>
                      {m.direction === "inbound" ? "Received" : "Sent"}
                    </span>
                    <span className="font-medium text-brand-ink text-sm">{m.candidate_name || m.candidate_phone}</span>
                  </div>
                  <div className="text-sm text-brand-muted truncate mt-1">{m.text}</div>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize flex-shrink-0 ${badge(m.status)}`}>
                  {m.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Overview;
